import { useReducer } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apm } from '@elastic/apm-rum'

import axios from '@pepper/utils/src/function/axios/editorInstance'
import extract from '@pepper/utils/src/function/extract'
import { useGlobalState, useShowMessage } from '@pepper/ui'

interface Parameter {
  data: {
    jobId: string | undefined
    count: number | undefined
    reProcess?: boolean
  }
  isBulkDownload?: boolean
}

export type DownloadStatus = 'inprogress' | 'finished' | 'reprocessing'

type Response = {
  status: DownloadStatus
  files?: [{ filename: string; size: number; url: string }]
  content?: string
  step_percent?: number
}

const REFETCH_INTERVAL = 15000
const SINGLE_DOWNLOAD_REFETCH_INTERVAL = 3000

async function bulkDownloadStatus(
  data: Parameter['data'],
  isBulkDownload: boolean
) {
  const queryParams = new URLSearchParams({
    re_process: (data.reProcess ?? false).toString()
  })
  if (!isBulkDownload) {
    queryParams.append('action', 'download')
  }

  const url = `/editor/bulk-export/${data.jobId}?${queryParams.toString()}`
  try {
    const response = await axios.get(url)
    return extract.data<Response>(response).data
  } catch (error) {
    throw new Error(extract.error(error).error)
  }
}

export function useGetBulkDownloadStatus({
  data,
  isBulkDownload = true
}: Parameter) {
  const showMessage = useShowMessage()
  const { setBulkDownloadJob, bulkDownloadJob } = useGlobalState()

  // trigger isReprocessed when step_percent is stuck at the same number for 2 consecutive requests
  // if step_percent remains the same even after re_process then fail the request
  const [state, dispatch] = useReducer(reducer, {
    reprocessStatus: false,
    reprocessApiFlag: false,
    previousData: undefined
  })

  return useQuery({
    enabled: data.jobId ? true : false,
    queryKey: ['bulk-docs-download', data.jobId],
    queryFn: () =>
      bulkDownloadStatus(
        {
          ...data,
          reProcess: state.reprocessApiFlag
        },
        isBulkDownload
      ),
    refetchInterval(_data) {
      if (!_data) return false
      if (!isBulkDownload)
        return ['inprogress', 'reprocessing'].includes(_data.status)
          ? SINGLE_DOWNLOAD_REFETCH_INTERVAL
          : false
      return ['inprogress', 'reprocessing'].includes(_data.status)
        ? REFETCH_INTERVAL
        : false
    },
    onSuccess(_data) {
      if (!isBulkDownload) return

      if (_data.status === 'finished') {
        dispatch({ type: 'SAVE_PREVIOUS_DATA', previousData: undefined })
        if (bulkDownloadJob) {
          setBulkDownloadJob({ ...bulkDownloadJob, status: 'success' })
        }

        return
      }

      if (_data.status === 'reprocessing') {
        dispatch({ type: 'REPROCESS_API_FLAG', reprocessApiFlag: false })
      }

      if (_data.status !== 'inprogress') return

      if (_data.step_percent === state.previousData?.step_percent) {
        if (state.reprocessStatus) {
          apm.captureError('Bulk download failed even after reprocessing')
          apm.addLabels({ jobId: data.jobId ?? '' })
          setBulkDownloadJob(null)
          showMessage({
            type: 'error',
            message: 'Something went wrong, please try again later'
          })
          return
        } else {
          dispatch({ type: 'REPROCESS_STATUS', reprocessStatus: true })
          dispatch({ type: 'REPROCESS_API_FLAG', reprocessApiFlag: true })
        }
      } else {
        dispatch({ type: 'REPROCESS_STATUS', reprocessStatus: false })
      }

      dispatch({ type: 'SAVE_PREVIOUS_DATA', previousData: _data })
    },
    onError() {
      if (isBulkDownload) {
        setBulkDownloadJob(null)
      }
    },
    refetchOnWindowFocus: false,
    retry: false
  })
}

type ReducerState = {
  reprocessStatus: boolean
  reprocessApiFlag: boolean
  previousData?: Response
}

type ReducerAction =
  | { type: 'SAVE_PREVIOUS_DATA'; previousData: Response | undefined }
  | { type: 'REPROCESS_API_FLAG'; reprocessApiFlag: boolean }
  | { type: 'REPROCESS_REQUEST' }
  | { type: 'REPROCESS_STATUS'; reprocessStatus: boolean }

function reducer(state: ReducerState, action: ReducerAction): ReducerState {
  switch (action.type) {
    case 'SAVE_PREVIOUS_DATA':
      return { ...state, previousData: action.previousData }
    case 'REPROCESS_API_FLAG':
      return { ...state, reprocessApiFlag: action.reprocessApiFlag }
    case 'REPROCESS_REQUEST':
      return { ...state, reprocessStatus: true }
    case 'REPROCESS_STATUS':
      return { ...state, reprocessStatus: action.reprocessStatus }

    default:
      return state
  }
}
